/**
 * The editor's form state and its mapping onto the wire (P4-T2).
 *
 * One editor serves cash and card ordinary entries (`UX-FLOWS.md` §4); the only field that
 * differs is the cash paid/unpaid state, which a card entry has no way to set — the API
 * refuses `paid` on a card row (`CARD_PAID_NOT_WRITABLE`), so the mapper omits it rather
 * than sending something that will be rejected.
 *
 * These are pure functions, so the validation and the sign rule ("expense negative, income
 * positive, and the user types a magnitude") are asserted in the fast suite instead of only
 * being exercised through a browser.
 */

import type {
  AccountCreateRequest,
  EntryCreateRequest,
  EntryBody,
  EntryPatchRequest,
} from '@saldo/contracts';
import { parseBusinessDate } from '@saldo/domain';

import { parseAmountToMinor } from './money';

export type EntryKind = 'income' | 'expense';
export type Currency = 'BRL' | 'CAD' | 'USD';
export type AccountRole = 'cash' | 'card';

export interface EntryForm {
  readonly kind: EntryKind;
  /** What the user typed: a positive magnitude, e.g. `12.34`. */
  readonly amount: string;
  readonly description: string;
  readonly accountId: string;
  readonly transactionDate: string;
  readonly dueDate: string;
  readonly paid: boolean;
  /**
   * The one optional category (P7-T1), or `null` for the first-class *uncategorized* state.
   *
   * `null` is a value and not a missing field: on an edit it is sent as `categoryId: null`, which
   * is the API's own way to *unset* a category, while an omitted field would leave it alone
   * (`entryPatchRequestSchema`).
   */
  readonly categoryId: string | null;
}

export interface AccountForm {
  readonly name: string;
  readonly currency: Currency;
  readonly role: AccountRole;
  readonly withOpening: boolean;
  readonly openingDate: string;
  /** Signed or zero: an opening is additive and may be `0` or negative. */
  readonly openingAmount: string;
}

export type Mapped<T> = { readonly ok: true; readonly body: T } | { readonly ok: false; readonly error: string };

const DESCRIPTION_LIMIT = 500;
const NAME_LIMIT = 120;

/** A strict business date, or an error naming the field — never a silent fallback to today. */
function businessDate(value: string, field: string): Mapped<string> {
  try {
    parseBusinessDate(value);
    return { ok: true, body: value };
  } catch {
    return { ok: false, error: `Enter a valid ${field} (YYYY-MM-DD).` };
  }
}

/**
 * The ordinary-entry create body.
 *
 * The amount is a magnitude and the *type* supplies the sign, which is the one place the
 * sign rule is applied in the UI: an expense is `-amount`, an income is `+amount`, and the
 * domain (`validateEntrySign`) is what would refuse anything else at the API.
 */
export function toEntryCreate(form: EntryForm, accountRole: AccountRole): Mapped<EntryCreateRequest> {
  const minor = parseAmountToMinor(form.amount, { allowNegative: false, allowZero: false });
  if (minor === null) return { ok: false, error: 'Enter a positive amount with at most two decimal places.' };

  const description = form.description.trim();
  if (description === '') return { ok: false, error: 'Enter a description.' };
  if (description.length > DESCRIPTION_LIMIT) {
    return { ok: false, error: `Keep the description under ${DESCRIPTION_LIMIT} characters.` };
  }
  if (form.accountId === '') return { ok: false, error: 'Choose an account.' };

  const transactionDate = businessDate(form.transactionDate, 'transaction date');
  if (!transactionDate.ok) return transactionDate;
  const dueDate = businessDate(form.dueDate, 'due date');
  if (!dueDate.ok) return dueDate;

  const signedMinor = form.kind === 'expense' ? `-${minor}` : minor;
  const body: EntryCreateRequest = {
    accountId: form.accountId,
    type: form.kind,
    signedMinor,
    description,
    transactionDate: transactionDate.body,
    dueDate: dueDate.body,
    // A card row is effective by its due date; `paid` is not writable on one.
    ...(accountRole === 'cash' ? { paid: form.paid } : {}),
    // Omitted rather than sent as `null`: on a create an absent category *is* uncategorized
    // (`DATABASE-PROPOSAL.md` §3), and the shape has no null member to send.
    ...(form.categoryId === null ? {} : { categoryId: form.categoryId }),
  };
  return { ok: true, body };
}

/**
 * The ordinary-entry edit body, and the cash pay.
 *
 * The account, the type and the currency are not writable here — an entry's currency is its
 * account's currency — so the account field is not read at all on an edit. The cash pay is
 * this same PATCH: `paid` plus the entry's own due date moved to the day the money actually
 * moved, with the transaction date and the amount left alone (`UX-FLOWS.md` §3).
 */
export function toEntryPatch(
  form: EntryForm,
  entry: EntryBody,
  accountRole: AccountRole,
): Mapped<EntryPatchRequest> {
  const minor = parseAmountToMinor(form.amount, { allowNegative: false, allowZero: false });
  if (minor === null) return { ok: false, error: 'Enter a positive amount with at most two decimal places.' };

  const description = form.description.trim();
  if (description === '') return { ok: false, error: 'Enter a description.' };
  if (description.length > DESCRIPTION_LIMIT) {
    return { ok: false, error: `Keep the description under ${DESCRIPTION_LIMIT} characters.` };
  }

  const transactionDate = businessDate(form.transactionDate, 'transaction date');
  if (!transactionDate.ok) return transactionDate;
  const dueDate = businessDate(form.dueDate, 'due date');
  if (!dueDate.ok) return dueDate;

  const signedMinor = form.kind === 'expense' ? `-${minor}` : minor;
  const body: EntryPatchRequest = {
    signedMinor,
    description,
    transactionDate: transactionDate.body,
    dueDate: dueDate.body,
    ...(accountRole === 'cash' ? { paid: form.paid } : {}),
    // The one optional category, and only where the row can carry one: a special row — an
    // opening, a transfer leg — has no category to keep, so the field stays out of the body
    // entirely rather than being sent as a `null` the row never had.
    ...(entry.type === 'income' || entry.type === 'expense' ? { categoryId: form.categoryId } : {}),
  };
  return { ok: true, body };
}

/**
 * The cash pay, as the same PATCH.
 *
 * `paymentDate` becomes the entry's due date — the date the money moved — and the
 * transaction date and amount are untouched, so marking a cash entry paid replaces its
 * pending effect once rather than doubling it.
 */
export function toPayPatch(entry: EntryBody, paymentDate: string): Mapped<EntryPatchRequest> {
  const date = businessDate(paymentDate, 'payment date');
  if (!date.ok) return date;
  if (entry.type !== 'income' && entry.type !== 'expense') {
    return { ok: false, error: 'Only an ordinary cash entry can be marked paid.' };
  }
  return { ok: true, body: { paid: true, dueDate: date.body } };
}

/**
 * The account create body, with its optional additive opening.
 *
 * The opening is creation-only composition on the account — there is no opening route —
 * and it is **signed or zero**, so this is the one amount field where a minus and a zero
 * are accepted.
 */
export function toAccountCreate(form: AccountForm): Mapped<AccountCreateRequest> {
  const name = form.name.trim();
  if (name === '') return { ok: false, error: 'Enter an account name.' };
  if (name.length > NAME_LIMIT) return { ok: false, error: `Keep the name under ${NAME_LIMIT} characters.` };

  if (!form.withOpening) return { ok: true, body: { name, currency: form.currency, kind: form.role } };

  const minor = parseAmountToMinor(form.openingAmount, { allowNegative: true, allowZero: true });
  if (minor === null) return { ok: false, error: 'Enter an opening amount, signed or zero, with at most two decimals.' };
  const date = businessDate(form.openingDate, 'opening date');
  if (!date.ok) return date;
  return {
    ok: true,
    body: { name, currency: form.currency, kind: form.role, opening: { date: date.body, signedMinor: minor } },
  };
}

/** The form state an editor opens with for an existing entry. */
export function formForEntry(entry: EntryBody, accountId: string): EntryForm {
  const minor = BigInt(entry.signedMinor);
  const magnitude = minor < 0n ? -minor : minor;
  return {
    kind: entry.type === 'income' ? 'income' : 'expense',
    amount: `${magnitude / 100n}.${(magnitude % 100n).toString().padStart(2, '0')}`,
    description: entry.description,
    accountId,
    transactionDate: entry.transactionDate ?? entry.effectiveDate,
    dueDate: entry.dueDate ?? entry.effectiveDate,
    paid: entry.paid === true,
    categoryId: entry.categoryId,
  };
}

/**
 * The form state an editor opens with for a new entry.
 *
 * One factory, so the empty draft has one definition: it is *uncategorized* and *unpaid by nothing*
 * — the amount is empty rather than zero, because `0` is not a legal ordinary amount and a
 * prefilled zero would be a value the user never typed. The cash `paid` default stays `true`
 * (`UX-FLOWS.md` §4: a cash entry is usually settled as it is entered); the card draft ignores it,
 * since `paid` is not writable on a card row.
 */
export function blankEntryForm(defaultAccountId: string, businessToday: string): EntryForm {
  return {
    kind: 'expense',
    amount: '',
    description: '',
    accountId: defaultAccountId,
    transactionDate: businessToday,
    dueDate: businessToday,
    paid: true,
    categoryId: null,
  };
}

